import { FC, useCallback, useMemo } from 'react'
import { Badge, Button, ConfigProvider, Flex, Pagination, Space, Table } from 'antd'
import PageLayout from '../../components/page-layout/page-layout'
import useEndpointQueryParams from '../../hooks/table-pages/use-endpoint-query-parameters'
import { getColumns } from './constants/columns'
import useTablePagination from '../../hooks/table-pages/use-table-pagination'
import { buildDesignerTasksDataSource } from './helpers/build-data-source'
import { getRowKey } from './helpers/row-helpers'
import TableWrapper from '../../components/table-wrapper/table-wrapper'
import useDesignerTasksRefresh from './hooks/use-designer-tasks-refresh'
import HelpDrawer from './components/help-drawer/help-drawer'
import FilterForm from './components/filters-form/filters-forms'
import useFiltersForm from '../../hooks/forms/use-filters-form'
import { deserializeFilters } from './helpers/deserialize-filters'
import useDesignerTasksScale from './hooks/use-designer-tasks-scale'
import ScaleSwitch from './components/scale-switch/scale-switch'
import { DesignerTasksScaleContext } from './constants/scale-context'
import { LARGE_SCALE_THEME } from './constants/large-scale-theme'

const DesignerTasksPage: FC = () => {
    const params = useEndpointQueryParams({ deserializeFilters })
    const { form: filterForm, onSearch, onClear } = useFiltersForm()
    const { data, newTasksCount, newStatusCount, handleRefresh } = useDesignerTasksRefresh(params)
    const pagination = useTablePagination(params, data)
    const { scale, setScale } = useDesignerTasksScale()
    const isLarge = scale === 'large'

    const dataSource = useMemo(() => buildDesignerTasksDataSource(data?.data ?? []), [data])

    const columns = useMemo(() => getColumns(newStatusCount, isLarge), [newStatusCount, isLarge])

    const tableFooter = useCallback(
        () => <Pagination {...pagination} size='small' align='end' />,
        [pagination]
    )

    return (
        <DesignerTasksScaleContext.Provider value={scale}>
            <ConfigProvider theme={isLarge ? LARGE_SCALE_THEME : undefined}>
                <PageLayout
                    title={'Задачи дизайнера'}
                    beforeUser={
                        <Space size={8}>
                            <HelpDrawer />
                            <Badge count={newTasksCount} overflowCount={99}>
                                <Button onClick={handleRefresh}>Обновить</Button>
                            </Badge>
                        </Space>
                    }
                >
                    <Flex
                        justify='space-between'
                        align='flex-start'
                        gap={12}
                        style={{ marginBottom: 12 }}
                    >
                        <FilterForm form={filterForm} onSearch={onSearch} onClear={onClear} />
                        <ScaleSwitch value={scale} onChange={setScale} />
                    </Flex>
                    <TableWrapper style={{ flex: 'auto' }} filtersSummaryHeight={0}>
                        <Table
                            bordered
                            columns={columns}
                            dataSource={dataSource}
                            pagination={false}
                            rowKey={getRowKey}
                            size={isLarge ? 'middle' : 'small'}
                            footer={tableFooter}
                        />
                    </TableWrapper>
                </PageLayout>
            </ConfigProvider>
        </DesignerTasksScaleContext.Provider>
    )
}

export default DesignerTasksPage
